import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { validatePassword } from '@/utils/password';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const regionOptions = [
  '北京', '上海', '广东', '江苏', '浙江', '山东', '河北', '河南',
  '四川', '湖北', '湖南', '福建', '安徽', '辽宁', '陕西', '重庆',
  '云南', '广西', '山西', '内蒙古', '贵州', '甘肃', '新疆', '吉林',
  '黑龙江', '海南', '宁夏', '青海', '西藏', '天津', '江西', '香港',
  '澳门', '台湾',
];

const inputCls =
  'w-full bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors';

export default function RegisterPage() {
  const [accountType, setAccountType] = useState<'personal' | 'company'>('company');
  const [formData, setFormData] = useState({
    companyName: '',
    creditCode: '',
    contactName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    region: '北京',
  });
  const [error, setError] = useState('');
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/profile';

  const set = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const { companyName, creditCode, contactName, phone, email, password, confirmPassword, region } = formData;

    if (!contactName || !phone || !email || !password) {
      setError('请填写所有必填字段');
      return;
    }
    if (accountType === 'company' && creditCode && creditCode.length !== 18) {
      setError('统一社会信用代码应为18位');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的手机号');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('请输入正确的邮箱地址');
      return;
    }
    const pwErr = validatePassword(password);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (password !== confirmPassword) {
      setError('两次密码输入不一致');
      return;
    }

    const result = await register({
      accountType,
      contactName,
      phone,
      email,
      region,
      password,
      ...(accountType === 'company' ? { companyName, creditCode } : {}),
    });

    if (result.success) {
      navigate(returnUrl, { replace: true });
    } else {
      setError(result.error || '注册失败，请重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50 min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="w-full max-w-lg mx-auto px-4 py-10 md:py-14">
          <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-add-line text-xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mt-4">创建账户</h1>
              <p className="text-foreground-500 text-sm mt-1.5">注册后即可发布供给与采购信息</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-md flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-xs">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              {(['company', 'personal'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setAccountType(t)}
                  className={`py-2.5 rounded-md border-2 text-sm font-medium transition-all cursor-pointer ${
                    accountType === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-background-200 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  {t === 'company' ? '企业账户' : '个人账户'}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {accountType === 'company' && (
                  <>
                    <div className="sm:col-span-2">
                      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                        企业名称 <span className="text-foreground-400 text-xs font-normal">选填</span>
                      </label>
                      <input type="text" value={formData.companyName}
                        onChange={(e) => set('companyName', e.target.value)}
                        placeholder="请输入企业全称" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                        统一社会信用代码 <span className="text-foreground-400 text-xs font-normal">选填</span>
                      </label>
                      <input type="text" value={formData.creditCode}
                        onChange={(e) => set('creditCode', e.target.value)}
                        placeholder="18位信用代码" maxLength={18} className={inputCls} />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">所在地区</label>
                  <select value={formData.region} onChange={(e) => set('region', e.target.value)}
                    className={inputCls + ' cursor-pointer'}>
                    {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    {accountType === 'company' ? '联系人' : '姓名'} <span className="text-accent-500">*</span>
                  </label>
                  <input type="text" value={formData.contactName}
                    onChange={(e) => set('contactName', e.target.value)}
                    placeholder="您的姓名" className={inputCls} />
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    手机号 <span className="text-accent-500">*</span>
                  </label>
                  <input type="tel" value={formData.phone} maxLength={11}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="用于登录和联系" className={inputCls} />
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input type="email" value={formData.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="example@example.com" className={inputCls} />
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    密码 <span className="text-accent-500">*</span>
                  </label>
                  <input type="password" value={formData.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder="≥8位，含大写、数字、特殊字符" className={inputCls} />
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    确认密码 <span className="text-accent-500">*</span>
                  </label>
                  <input type="password" value={formData.confirmPassword}
                    onChange={(e) => set('confirmPassword', e.target.value)}
                    placeholder="请再次输入密码" className={inputCls} />
                </div>
              </div>

              <button type="submit" disabled={isLoading}
                className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                {isLoading ? '注册中...' : '完成注册'}
              </button>

              <p className="text-foreground-400 text-xs text-center pt-1">
                注册即表示您同意平台的
                <Link to="/agreement/user" className="text-primary-600 hover:text-primary-700 mx-0.5">用户协议</Link>
                和
                <Link to="/agreement/privacy" className="text-primary-600 hover:text-primary-700 ml-0.5">隐私政策</Link>
              </p>
            </form>

            <div className="mt-6 text-center">
              <p className="text-foreground-400 text-sm">
                已有账户？
                <Link to="/login" className="text-primary-600 hover:text-primary-700 ml-1 font-medium">立即登录</Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
